import { Injectable } from '@angular/core';
import { Geolocation } from '@capacitor/geolocation';

export interface LatLng { lat: number; lng: number; }

@Injectable({ providedIn: 'root' })
export class LocationService {
  private cached: LatLng | null = null;
  private inFlight: Promise<LatLng> | null = null;

  /** Returns cached location instantly, or fetches if not yet available. */
  async getLocation(): Promise<LatLng> {
    if (this.cached) return this.cached;
    if (this.inFlight) return this.inFlight;

    this.inFlight = this.fetch();
    try {
      this.cached = await this.inFlight;
      return this.cached;
    } finally {
      this.inFlight = null;
    }
  }

  /** Force a fresh GPS fix and update the cache. */
  async refresh(): Promise<LatLng> {
    this.inFlight = this.fetch();
    try {
      this.cached = await this.inFlight;
      return this.cached;
    } finally {
      this.inFlight = null;
    }
  }

  get lastKnown(): LatLng | null { return this.cached; }

  /** Let callers (e.g. Buzz page) populate the cache after their own GPS fetch. */
  setLastKnown(coords: LatLng): void { this.cached = coords; }

  private async fetch(): Promise<LatLng> {
    try {
      const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
      return { lat: pos.coords.latitude, lng: pos.coords.longitude };
    } catch {
      return new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(
          r => resolve({ lat: r.coords.latitude, lng: r.coords.longitude }),
          reject,
          { timeout: 5000 },
        )
      );
    }
  }
}
